import {
  AccountResource,
  CurrencyExchangeSourceAndTargetCurrencyManager,
  Loadable,
  LoadableState,
  SourceCurrencyList,
  TargetCurrencyList,
} from '@eih-netbroker/common';
import { atom, useAtomValue } from 'jotai';
import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useAccounts } from '../../../shared/services/useAccounts';
import { useResource, useResourceHeaders } from '../../../shared/services/useResource';
import { store } from '../../../shared/state/store';
import { createLogger } from '../../../shared/utils/logger';
import { getExchangePeriodHours } from '../../../shared/utils/useLocalStorage';
import { CurrencyExchangeSourceAndTargetCurrencyManagerContext } from '../../core/components/cash-popover/CurrencyExchangeSourceAndTargetCurrencyManagerContext';

export type OrderCurrencyExchangeSourceAndTargetCurrencyManagerProps = {
  orderCurrencyExchangeSourceCurrencies: LoadableState<SourceCurrencyList>;
  orderCurrencyExchangeActiveSourceCurrencies: LoadableState<string[]>;
};

export const OrderCurrencyExchangeSourceAndTargetCurrencyManagerContext =
  createContext<OrderCurrencyExchangeSourceAndTargetCurrencyManagerProps>({
    orderCurrencyExchangeSourceCurrencies: Loadable.uninitialized,
    orderCurrencyExchangeActiveSourceCurrencies: Loadable.uninitialized,
  });

const logger = createLogger('CurrencyExchangeEvents');
const exchangePeriodHours = getExchangePeriodHours();

export const OrderCurrencyExchangeSourceAndTargetCurrencyManagerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { translation } = useLocale();
  const { getResourceHeaderWithTokenForAccount } = useResourceHeaders();
  const { selectedAccountOrClient } = useAccounts();
  const accountResource = useResource(AccountResource);
  const selectedAccountNumberRef = useRef<string | undefined>(selectedAccountOrClient.account?.accountNumber);

  selectedAccountNumberRef.current = selectedAccountOrClient.account?.accountNumber;

  const getCurrentAccountNumber = useCallback((): string => {
    if (!selectedAccountNumberRef.current) {
      console.error('Account number is required for order currency exchange');
      return '';
    }

    return selectedAccountNumberRef.current;
  }, []);
  /**
   * [Order currency exchange] Available source currencies for currency exchange
   */
  const orderCurrencyExchangeSourceCurrenciesAtom = useMemo(
    () => atom<LoadableState<SourceCurrencyList>>(Loadable.uninitialized),
    [],
  );

  const orderCurrencyExchangeSourceCurrencies = useAtomValue(orderCurrencyExchangeSourceCurrenciesAtom);

  /**
   * [Order currency exchange] Active source currencies for currency exchange
   */
  const orderCurrencyExchangeActiveSourceCurrenciesAtom = useMemo(
    () => atom<LoadableState<string[]>>(Loadable.uninitialized),
    [],
  );

  const orderCurrencyExchangeActiveSourceCurrencies = useAtomValue(orderCurrencyExchangeActiveSourceCurrenciesAtom);

  const currencyExchangeEvents = useRef(
    CurrencyExchangeSourceAndTargetCurrencyManager.getInstance({
      getSourceCurrencies: (targetCurrency?: string) =>
        accountResource.getSourceCurrencies(
          { targetCcyCode: targetCurrency },
          getResourceHeaderWithTokenForAccount(getCurrentAccountNumber()),
        ),
      getTargetCurrencies: (currency: string) =>
        accountResource.getTargetCurrencies(
          { sourceCcyCode: currency },
          getResourceHeaderWithTokenForAccount(getCurrentAccountNumber()),
        ),
      messages: {
        loadSourceCurrenciesFailed: translation.currencyChange.error.loadSourceCurrenciesFailed,
        loadTargetCurrenciesFailed: translation.currencyChange.error.loadTargetCurrenciesFailed,
      },
      settersGetters: {
        get activeSourceCurrencies() {
          return store.get(orderCurrencyExchangeActiveSourceCurrenciesAtom);
        },
        set activeSourceCurrencies(value: LoadableState<string[]>) {
          store.set(orderCurrencyExchangeActiveSourceCurrenciesAtom, value);
        },
        get sourceCurrencies() {
          return store.get(orderCurrencyExchangeSourceCurrenciesAtom);
        },
        set sourceCurrencies(value: LoadableState<SourceCurrencyList>) {
          store.set(orderCurrencyExchangeSourceCurrenciesAtom, value);
        },
        get targetCurrencies() {
          throw new Error(`Target currencies cannot be used in order currency exchange`);
        },
        set targetCurrencies(value: LoadableState<TargetCurrencyList>) {
          throw new Error(`Target currencies cannot be used in order currency exchange`);
        },
      },
      exchangePeriodHours,
      logger,
    }),
  );

  // since the currencyExchangeEvents is a non react instance, we need to update the hook's refs
  useEffect(() => {
    currencyExchangeEvents.current.updateRefs(
      (targetCurrency?: string) =>
        accountResource.getSourceCurrencies(
          { targetCcyCode: targetCurrency },
          getResourceHeaderWithTokenForAccount(getCurrentAccountNumber()),
        ),
      (currency: string) =>
        accountResource.getTargetCurrencies(
          { sourceCcyCode: currency },
          getResourceHeaderWithTokenForAccount(getCurrentAccountNumber()),
        ),
    );
  }, [accountResource, getCurrentAccountNumber, getResourceHeaderWithTokenForAccount]);

  return (
    <OrderCurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider
      value={{ orderCurrencyExchangeSourceCurrencies, orderCurrencyExchangeActiveSourceCurrencies }}
    >
      <CurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider value={{ currencyExchangeEvents }}>
        {children}
      </CurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider>
    </OrderCurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider>
  );
};
